import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Star, User, CreditCard, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Link } from 'react-router-dom';

interface Session {
  id: string;
  therapist_name: string;
  scheduled_at: string;
  duration_minutes: number;
  session_type: 'online' | 'in_person';
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  price: number;
}

// Real sessions will be loaded from Supabase
const mockSessions: Session[] = [];

export const ClientPortal = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Load user sessions
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('sessions')
        .select(`
          *,
          therapists(
            id,
            bio
          )
        `)
        .eq('client_id', user?.id)
        .order('scheduled_at', { ascending: false });

      if (sessionsError) throw sessionsError;

      // Transform data to match our interface
      const transformedSessions = sessionsData?.map(session => ({
        id: session.id,
        therapist_name: `Professional ${session.therapist_id?.substring(0, 8)}`, // Fallback name
        scheduled_at: session.scheduled_at,
        duration_minutes: session.duration_minutes,
        session_type: session.session_type,
        status: session.status,
        price: Number(session.price)
      })) || [];

      setSessions(transformedSessions);
      
      // For now, set a default balance (would be calculated from payments)
      setBalance(250);
      
    } catch (error: any) {
      console.error('Error loading user data:', error);
      toast({
        title: "Error loading data",
        description: "There was a problem loading your information.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const upcomingSessions = sessions.filter(s => s.status === 'scheduled');
  const completedSessions = sessions.filter(s => s.status === 'completed');

  const getStatusBadge = (status: string) => {
    const variants = {
      scheduled: 'default',
      completed: 'secondary',
      cancelled: 'destructive',
      no_show: 'destructive'
    } as const;
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'default'}>
        {t(`client.session.${status}`)}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background/50 p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('client.portal.welcome')} {user?.user_metadata?.first_name}
          </h1>
          <p className="text-muted-foreground">
            {t('client.portal.subtitle')}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('client.stats.balance')}
                  </p>
                  <p className="text-2xl font-bold text-primary">${balance}</p>
                </div>
                <CreditCard className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('client.stats.upcoming')}
                  </p>
                  <p className="text-2xl font-bold text-primary">{upcomingSessions.length}</p>
                </div>
                <Calendar className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('client.stats.completed')}
                  </p>
                  <p className="text-2xl font-bold text-primary">{completedSessions.length}</p>
                </div>
                <Star className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t('client.stats.messages')}
                  </p>
                  <p className="text-2xl font-bold text-primary">3</p>
                </div>
                <MessageCircle className="h-8 w-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="sessions" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="sessions">{t('client.tabs.sessions')}</TabsTrigger>
            <TabsTrigger value="therapists">{t('client.tabs.therapists')}</TabsTrigger>
            <TabsTrigger value="payments">{t('client.tabs.payments')}</TabsTrigger>
            <TabsTrigger value="profile">{t('client.tabs.profile')}</TabsTrigger>
          </TabsList>

          <TabsContent value="sessions" className="space-y-6">
            {/* Upcoming Sessions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t('client.sessions.upcoming')}
                </CardTitle>
                <CardDescription>
                  {t('client.sessions.upcomingDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {upcomingSessions.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingSessions.map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                            <User className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{session.therapist_name}</h3>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span>{new Date(session.scheduled_at).toLocaleString()}</span>
                              <Badge variant="outline">{session.session_type}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold">${session.price}</span>
                          {getStatusBadge(session.status)}
                          <Button size="sm">{t('client.sessions.join')}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('client.sessions.noUpcoming')}</p>
                    <Button className="mt-4" asChild>
                      <Link to="/find">{t('client.sessions.bookNew')}</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Sessions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('client.sessions.recent')}</CardTitle>
                <CardDescription>
                  {t('client.sessions.recentDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {completedSessions.length > 0 ? (
                  <div className="space-y-4">
                    {completedSessions.map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-12 h-12 bg-secondary/20 rounded-full">
                            <User className="h-6 w-6 text-secondary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{session.therapist_name}</h3>
                            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              <span>{new Date(session.scheduled_at).toLocaleString()}</span>
                              <Badge variant="outline">{session.session_type}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold">${session.price}</span>
                          {getStatusBadge(session.status)}
                          <Button variant="outline" size="sm">{t('client.sessions.review')}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('client.sessions.noRecent')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="therapists">
            <Card>
              <CardHeader>
                <CardTitle>{t('client.therapists.favorites')}</CardTitle>
                <CardDescription>
                  {t('client.therapists.favoritesDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                  <div className="text-center py-8">
                    <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('client.therapists.noFavorites')}</p>
                    <Button className="mt-4" asChild>
                      <Link to="/find">{t('client.therapists.browse')}</Link>
                    </Button>
                  </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>{t('client.payments.history')}</CardTitle>
                <CardDescription>
                  {t('client.payments.historyDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('client.payments.noHistory')}</p>
                  <Button className="mt-4">{t('client.payments.addFunds')}</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>{t('client.profile.settings')}</CardTitle>
                <CardDescription>
                  {t('client.profile.settingsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">Profile Settings</h3>
                      <p className="text-sm text-muted-foreground">Manage your personal information</p>
                    </div>
                    <Button variant="outline" asChild>
                      <Link to="/client-portal/profile">Edit Profile</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">Messages</h3>
                      <p className="text-sm text-muted-foreground">View and manage your messages</p>
                    </div>
                    <Button variant="outline" asChild>
                      <Link to="/client-portal/messages">View Messages</Link>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">Find AI Therapist</h3>
                      <p className="text-sm text-muted-foreground">Get AI-powered therapist recommendations</p>
                    </div>
                    <Button variant="outline" asChild>
                      <Link to="/client-portal/find-therapist-ai">Find Therapist</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};